import React, { useState } from 'react';
import { Button, Modal, Alert } from 'react-bootstrap';
import { Trash2, Edit2, ExternalLink } from 'lucide-react';
import { ProjectModel, ProjectStatus } from '../../../types/projects';
import { EditableTextField } from '../../../Components/EditableTextField';
import { getStatusIcon, getStatusLabel, getStatusColor } from '../../../Helpers/ProjectUtils';
import { ProjectEndDateModal } from '../modals/ProjectEndDateModal';
import { ProjectStatusModal } from '../modals/ProjectStatusModal';
import { BuildingGroupTags } from '../../../Components/BuildingGroupTags';
import { GroupTag } from '../../../types/protocols';

interface ProjectHeaderSectionProps {
    project: ProjectModel;
    onUpdateTitle: (title: string) => Promise<void>;
    onUpdateStatus: (status: ProjectStatus, actualEndDate?: string) => Promise<void>;
    onReopenProject: () => Promise<void>;
    onDelete: () => Promise<void>;
    submitting?: boolean;
    buildingName?: string;
    buildingCity?: string;
    buildingMunicipality?: string;
    buildingGroupTags?: { [key: string]: GroupTag };
    coverPhotoSrc?: string;
}

export const ProjectHeaderSection: React.FC<ProjectHeaderSectionProps> = ({
    project,
    onUpdateTitle,
    onUpdateStatus,
    onReopenProject,
    onDelete,
    submitting = false,
    buildingName,
    buildingCity,
    buildingMunicipality,
    buildingGroupTags,
    coverPhotoSrc,
}) => {
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showEndDateModal, setShowEndDateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingStatus, setPendingStatus] = useState<ProjectStatus | null>(null);
    const [statusError, setStatusError] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const handleStatusChange = (newStatus: ProjectStatus) => {
        if (newStatus === ProjectStatus.COMPLETED && project.status !== ProjectStatus.COMPLETED) {
            setPendingStatus(newStatus);
            setShowStatusModal(false);
            setShowEndDateModal(true);
        } else if (project.status === ProjectStatus.COMPLETED && newStatus !== ProjectStatus.COMPLETED) {
            onReopenProject();
            onUpdateStatus(newStatus);
        } else {
            onUpdateStatus(newStatus);
        }
    };

    const handleEndDateConfirm = async (endDate: string) => {
        if (pendingStatus) {
            setStatusError(null);
            try {
                await onUpdateStatus(pendingStatus, endDate);
                setPendingStatus(null);
                setShowEndDateModal(false);
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Kunde inte ändra status';
                setStatusError(message);
            }
        }
    };

    const handleEndDateCancel = () => {
        setShowEndDateModal(false);
        setPendingStatus(null);
        setStatusError(null);
    };

    const handleDeleteConfirm = async () => {
        setDeleteError(null);
        try {
            await onDelete();
            setShowDeleteModal(false);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Kunde inte radera projektet';
            setDeleteError(message);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setDeleteError(null);
    };

    return (
        <>
            <div style={{ marginBottom: '24px' }}>
                {/* Title row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <img src="/Images/UI/projekt.png" alt="" style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                        <EditableTextField
                            value={project.title}
                            onSave={onUpdateTitle}
                            placeholder="Projekttitel"
                            required
                            disabled={submitting}
                            displayStyle={{ fontSize: '24px', fontWeight: 600 }}
                        />
                    </div>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setShowDeleteModal(true)}
                        disabled={submitting}
                        style={{ flexShrink: 0 }}
                    >
                        <Trash2 size={16} className="me-1" />
                        Radera
                    </Button>
                </div>

                {/* Building info row */}
                {buildingName && (
                    <div className="project-header-building-row">
                        {coverPhotoSrc && (
                            <img
                                src={coverPhotoSrc}
                                alt={buildingName}
                                className="project-header-building-thumbnail"
                            />
                        )}
                        <div className="project-header-building-info">
                            <a
                                href={`/building/${project.buildingKey}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="project-header-building-link"
                            >
                                <img src="/Images/UI/building.png" alt="" className="project-header-building-icon" />
                                {buildingName}
                                <ExternalLink size={12} />
                            </a>
                            {(buildingCity || buildingMunicipality) && (
                                <div className="project-header-location-row">
                                    <img src="/Images/UI/location.png" alt="" className="project-header-location-icon" />
                                    <span className="project-header-location-text">
                                        {[buildingCity, buildingMunicipality].filter(Boolean).join(', ')}
                                    </span>
                                </div>
                            )}
                            {buildingGroupTags && Object.keys(buildingGroupTags).length > 0 && (
                                <div className="project-header-tags-row">
                                    <BuildingGroupTags groupTags={buildingGroupTags} />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Status row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                        style={{
                            backgroundColor: getStatusColor(project.status),
                            color: '#ffffff',
                            padding: '4px 12px',
                            borderRadius: '9999px',
                            fontSize: '14px',
                            fontWeight: '500',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}
                    >
                        {getStatusIcon(project.status)}
                        {getStatusLabel(project.status)}
                    </div>
                    <Button
                        size="sm"
                        variant="link"
                        onClick={() => setShowStatusModal(true)}
                        disabled={submitting}
                        style={{ padding: '2px 6px', color: '#666' }}
                    >
                        <Edit2 size={14} />
                    </Button>
                </div>
            </div>

            <ProjectStatusModal
                show={showStatusModal}
                onHide={() => setShowStatusModal(false)}
                currentStatus={project.status}
                onStatusChange={handleStatusChange}
                submitting={submitting}
            />

            <ProjectEndDateModal
                show={showEndDateModal}
                onHide={handleEndDateCancel}
                onConfirm={handleEndDateConfirm}
                submitting={submitting}
                error={statusError}
            />

            <Modal show={showDeleteModal} onHide={handleDeleteCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Radera projekt</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteError && (
                        <Alert variant="danger" className="mb-3">
                            {deleteError}
                        </Alert>
                    )}
                    Är du säker på att du vill radera projektet "{project.title}"? Denna åtgärd kan inte ångras.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleDeleteCancel}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm}>
                        Radera
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
